import {Component, OnInit} from "@angular/core";
import {Location} from '@angular/common';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {DbValidators} from "../../shared/validators/DbValidators";
import {DbResponse} from "../../shared/interceptors/db-response";
import {ActivatedRoute, Router} from "@angular/router";
import {AuthService} from "../auth.service";
import {DbServerErrors} from "../../shared/interceptors/db-server-errors";
import {DbTextPipe} from "../../shared/pipes/db-text.pipe";
import {Utils} from "../../shared/utils/utils";
import {CompanyInfo} from '../../store/actions/company-info.action';
import {debounceTime, distinctUntilChanged, filter, first} from "rxjs/operators";

@Component({
    selector: 'db-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    standalone: false
})
export class ResetPasswordComponent implements OnInit {

  public loading: boolean;
  public isMobile: boolean;

  private readonly token: string;
  private readonly email: string;

  public resetPasswordForm: FormGroup;
  public formSubmitted: boolean;
  public passwordsMatch = true;
  public serverErrors: DbServerErrors;
  public showPassword: boolean = false;
  public showConfirmPassword: boolean = false;
  public hasServerFieldErrors: boolean = false;

  public showPWDRequirements: boolean = false;
  public progress: number = 0;
  public companyInfo: CompanyInfo;

  constructor(
    private _location: Location,
    private _router: Router,
    private _route: ActivatedRoute,
    private _dbText: DbTextPipe,
    private _authService: AuthService
  ) {
    this.resetPasswordForm = new FormGroup({
      'password': new FormControl('',
      [Validators.required, Validators.minLength(8), DbValidators.trim,
        Utils.patternValidator(/\d/, { hasNumber: true }),
        Utils.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
        Utils.patternValidator(/[a-z]/, { hasSmallCase: true })
      ]),
      'confirmPassword': new FormControl('', [Validators.required, DbValidators.confirmPassword, DbValidators.trim])
    });

    const checkPasswordsMatch = () => {
      const passwordValue = this.resetPasswordForm.get('password').value;
      const confirmPasswordValue = this.resetPasswordForm.get('confirmPassword').value;
      this.passwordsMatch = passwordValue === confirmPasswordValue;
    };

    this.resetPasswordForm.get('password').valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(checkPasswordsMatch);

    this.resetPasswordForm.get('confirmPassword').valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(checkPasswordsMatch);

    this.formSubmitted = false;

    const queryParams: any = (<any>this._route.queryParams).value;
    this.token = queryParams.token;
    this.email = queryParams.email && queryParams.email.indexOf(' ') > -1 ? queryParams.email.replace(/\s+/g, '+') : queryParams.email;

    if (!this.token || !this.email) {
      this._router.navigate(['/login']);
    }

    let queryParamsIndex: number = this._router.url.indexOf('?');
    if (queryParamsIndex !== -1) {
      this._location.replaceState(_router.url.substring(0, queryParamsIndex));
    }
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 991;
    this.companyInfo = this._route.snapshot.data['companyInfo'];
  }


  public togglePassword() {
    this.showPassword = !this.showPassword;
  }

  public toggleConfirmPassword() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  public onResetPasswordSubmit(): void {
    this.formSubmitted = true;

    if (this.resetPasswordForm.controls['password'].valid && this.passwordsMatch) {
      this.loading = true;
      this.serverErrors = null;
      this.hasServerFieldErrors = null;
      this._authService.resetPassword(this.token, this.email, this.resetPasswordForm.value.password, this.resetPasswordForm.value.confirmPassword)
        .pipe(filter((response: DbResponse): boolean => !!response),
          first())
        .subscribe((response: DbResponse): void => {
          if (response.success) {
            this._authService.logout('/login?fromResetPassword=fromResetPassword', null, null, false);
            this.loading = false;
          }
          else {
            this.loading = false;
            this.serverErrors = response.serverErrors;
            this.checkHasServerFieldErrors(response.serverErrors);
          }
        });
    }
  }

  private checkHasServerFieldErrors(serverErrors: DbServerErrors): void {
    this.hasServerFieldErrors = !!Utils.intersect(Object.keys(serverErrors.fields), ['password', 'confirmPassword']).length;
  }

  public progressPercent(): void {
    this.progress = Utils.progressPercent(this.resetPasswordForm.controls['password']);
  }
}
